// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@sc/App/Components/Common/XngZone";
import { CustomAppState } from "@sc/App/Store/Reducers";
import ToggleFilters from "@sc/App/Widgets/ToggleFilters/ToggleFiltersComponent";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

const mapStateToProps = (state: CustomAppState) => ({
    isTabletScreen: state.xCommon.isTabletScreen,
    filterDrawerIsOpen: state.xListFilter.filterDrawerIsOpen,
});

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export interface ToggleFiltersWidgetStyles {
    filterWrapper?: InjectableCss;
    filtersDrawer?: DrawerPresentationProps;
    controlsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    filterBtn?: LinkPresentationProps;
}
export const toggleFiltersWidgetStyles: ToggleFiltersWidgetStyles = {};

const styles = toggleFiltersWidgetStyles;

const ToggleFiltersWidget = ({ id, fields, isTabletScreen, filterDrawerIsOpen }: Props) => {
    const dispatch = useDispatch();

    const handleCloseFilterDrawer = () => {
        dispatch({
            type: "xListFilter/FilterDrawer/SetFilterDrawerIsOpen",
            filterDrawerIsOpen: false,
        });
    };

    const handleOpenFilterDrawer = () => {
        dispatch({
            type: "xListFilter/FilterDrawer/SetFilterDrawerIsOpen",
            filterDrawerIsOpen: true,
        });
    };
    useEffect(() => {
        handleCloseFilterDrawer();
    }, []);

    return (
        <>
            {!isTabletScreen ? (
                <ToggleFilters expandByDefault={fields.expandByDefault} type="widget">
                    <XngZone contentId={id} zoneName="filtersZone" hint="Zone for Filters" />
                </ToggleFilters>
            ) : (
                <Link
                    aria-label="open filter drawer"
                    data-test-selector="filter-drawer-trigger"
                    {...styles.filterBtn}
                    onClick={handleOpenFilterDrawer}
                >
                    {translate("Filter Results")}
                </Link>
            )}
            <Drawer
                isOpen={isTabletScreen && filterDrawerIsOpen}
                handleClose={handleCloseFilterDrawer}
                position="left"
                draggable={false}
                contentLabel="filter drawer"
                headline={translate("Filter Results")}
                {...styles.filtersDrawer}
            >
                <StyledWrapper {...styles.filterWrapper}>
                    <XngZone contentId={id} zoneName="filtersZone" hint="Zone for Filters" />
                </StyledWrapper>
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ToggleFiltersWidget),
    definition: {
        displayName: "Toggle Filters",
        group: XNGAGE_CUSTOM_WIDGET,
        allowedContexts: [
            "OrderHistoryPage",
            "VmiOrderHistoryPage",
            "SavedOrderListPage",
            "OrderApprovalListPage",
            "InvoiceHistoryPage",
            "MyListsPage",
            "RfqMyQuotesPage",
        ],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
